import {
  IAutomationActionTemplateLessonlyAssignToContent,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import { LessonlyContentType } from '@dadohr/shared/domainTypes/integrations/lessonly';
import { InputAdornment } from '@material-ui/core';
import React, { SetStateAction } from 'react';
import { AutocompleteSingle } from '../../../../../../../components/Autocomplete';
import { ColoredChip } from '../../../../../../../components/ColoredChip';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import { getLessonlyContentOptions } from '../../../../../../Org/pages/Integrations/integrations/lessonly/service';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import { ActionActorSelectorMulti, actionActorToString } from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  VALIDATORS_LESSONLY_ASSIGN_TO_CONTENT,
  useFieldValidator
} from '../../validation';

// Older templates may still hold 'certification' — a certification is a
// Lessonly path, and anything that isn't a lesson is assigned as one.
const LessonlyContentTypeChip = ({
  type
}: {
  type: IAutomationActionTemplateLessonlyAssignToContent['content']['type'];
}) => {
  const { t } = useUnsafeTranslation();
  const width = 70;
  return type === LessonlyContentType.LESSON ? (
    <ColoredChip width={width} color="yellow" mode="light" size="small">
      {t('Lesson')}
    </ColoredChip>
  ) : (
    <ColoredChip width={width} color="blue" mode="light" size="small">
      {t('Path')}
    </ColoredChip>
  );
};

const ContentSelector = ({
  orgId,
  value,
  onChange,

  helperText,
  error
}: {
  orgId: string;
  value: IAutomationActionTemplateLessonlyAssignToContent['content'];
  onChange: (
    nextValue: IAutomationActionTemplateLessonlyAssignToContent['content']
  ) => void;

  helperText?: React.ReactNode;
  error?: boolean;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <AutocompleteSingle
      label={t('Assign to')}
      fullWidth
      value={value}
      onChange={(nextValue) => {
        if (nextValue) {
          onChange(nextValue);
        }
      }}
      helperText={helperText}
      error={error}
      disabledClearable={true}
      options={() =>
        getLessonlyContentOptions(orgId).then((cs) =>
          cs.map<IAutomationActionTemplateLessonlyAssignToContent['content']>(
            (c) => ({ id: c.id, name: c.title, type: c.type })
          )
        )
      }
      getOptionLabel={(o) => o.name}
      renderOption={(o) => (
        <FlexContainer>
          <LessonlyContentTypeChip type={o.type} />
          <div>{o.name}</div>
        </FlexContainer>
      )}
      InputProps={{
        startAdornment: value.id ? (
          <InputAdornment position="start">
            <LessonlyContentTypeChip type={value.type} />
          </InputAdornment>
        ) : null
      }}
    />
  );
};

export const ActionSettingsLessonlyAssignToContent = ({
  value,
  onChange,
  expTpl,
  aTpl,
  validation,
  setValidation
}: {
  value: IAutomationActionTemplateLessonlyAssignToContent;
  onChange: (
    nextValue: IAutomationActionTemplateLessonlyAssignToContent
  ) => void;
  aTpl: IAutomationTemplate;
  expTpl: TaskBuilderContext;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
}) => {
  const { t } = useUnsafeTranslation();

  const validatePplToAdd = useFieldValidator(
    setValidation,
    VALIDATORS_LESSONLY_ASSIGN_TO_CONTENT,
    'pplToAdd'
  );
  const invalidPpltoAdd = validation?.fields.pplToAdd || null;

  const validateContent = useFieldValidator(
    setValidation,
    VALIDATORS_LESSONLY_ASSIGN_TO_CONTENT,
    'content'
  );
  const invalidContent = validation?.fields.content || null;

  return (
    <div>
      <FormRow>
        <ActionActorSelectorMulti
          label={t('Who should be assigned')}
          value={value.pplToAdd}
          onChange={(nextPplToAdd) => {
            const nextValue = { ...value, pplToAdd: nextPplToAdd };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
          expTpl={expTpl}
          fullWidth
          error={!!invalidPpltoAdd}
          helperText={invalidPpltoAdd}
          onBlur={() => validatePplToAdd(value, expTpl, aTpl)}
        />
      </FormRow>
      <FormRow>
        <ContentSelector
          orgId={expTpl.orgId}
          value={value.content}
          helperText={invalidContent}
          error={!!invalidContent}
          onChange={(nextContent) => {
            const nextValue = { ...value, content: nextContent };
            validateContent(nextValue, expTpl, aTpl);
            onChange(nextValue);
          }}
        />
      </FormRow>
    </div>
  );
};

export const ActionSettingsLessonlyAssignToContentReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateLessonlyAssignToContent;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Who should be assigned')}>
          {joinWithCommaAndAnd(
            value.pplToAdd.map((p) =>
              actionActorToString(p, expTpl.supportingActors, t)
            ),
            t
          )}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Assign to')}>
          {value.content.name}
        </DataFieldWithLabel>
      </FormRow>
    </div>
  );
};
